import { Enum, ModuleOptions, UIBasePart } from '@/api/airchef/Api';
import { InvalidPathValueError, InvalidStateError } from '@/common/errors';
import { isMeasurement } from '@/common/measurements';
import { getCurrentTimestamp, isEnum, isModuleOptions, simpleClone } from '@/common/utils';
import {
  Measurement,
  MeasurementType,
  PropertiesEquationOutputOperation,
  UnitOfMeasurement,
  ValidUIElement,
  ValidUIField,
} from '@/types/helpers';

import { AlignedData } from 'uplot';

import jp from 'jsonpath';
import {
  applyAppend,
  applyInsert,
  applyMerge,
  applyRemoveKey,
  applyRemoveKeys,
  applyRemoveValue,
  applyRemoveValues,
} from '@/hooks/socket/apply';

const REGEX_UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;

export function getChartFieldValue(foundValue: unknown): AlignedData | ChartDataTemplate {
  if (foundValue === null || foundValue === undefined) {
    return [];
  }

  if (Array.isArray(foundValue)) {
    return foundValue as AlignedData;
  }

  if (isChartData(foundValue)) {
    return foundValue;
  }

  return [];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getFieldValue<_T extends 'module'>(
  foundValue: ModuleOptions | undefined,
  options: ValidUIField,
): ModuleOptions;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getFieldValue<_T extends 'enum'>(foundValue: Enum | undefined, options: ValidUIField): Enum;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getFieldValue<_T extends 'relationship'>(
  foundValue: string[] | string | undefined,
  options: ValidUIField,
): string[] | string;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getFieldValue<_T extends 'number'>(foundValue: number | undefined, options: ValidUIField): number;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getFieldValue<_T extends 'text' | 'date' | 'time'>(
  foundValue: string | undefined,
  options: ValidUIField,
): string;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getFieldValue<_T extends 'toggle'>(foundValue: boolean | undefined, options: ValidUIField): boolean;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getFieldValue<_T extends 'select', V extends string | number | undefined>(
  foundValue: V | undefined,
  options: ValidUIField,
): V extends undefined ? string | number : V;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getFieldValue<_T extends 'measurement', M extends MeasurementType = MeasurementType>(
  foundValue: Measurement<M> | undefined,
  options: ValidUIField,
): Measurement<M>;
export function getFieldValue<M extends MeasurementType>(
  foundValue: number | string | boolean | Measurement<M> | string[] | Enum | ModuleOptions | undefined,
  options: ValidUIField,
): number | string | boolean | Measurement<M> | Enum | ModuleOptions | string[] {
  if (foundValue === undefined) {
    // Return a default value
    switch (options.type) {
      case 'text':
      case 'textarea':
      case 'bookmark':
        return '';
      case 'toggle':
        return false;
      case 'select':
      case 'radioButtons':
        return options.options.length > 0 ? options.options[0].value : '';
      case 'selectNumber':
        return options.options.length > 0 ? options.options[0].value : 0;
      case 'number':
        return 0.0;
      case 'time':
        return '00:00';
      case 'date':
        return getCurrentTimestamp();
      case 'measurement':
        return {
          value: 0,
          type: options.measurementType,
          unit: getDefaultUnit(options.measurementType as MeasurementType),
        } as Measurement<M>;
      case 'relationship':
        return options.multiple ? [] : '';
      case 'module':
        return {
          modulesAvailable: [],
          modulesSelected: [],
        };
      case 'selectEnum':
        return { value: { value: 'none', label: { en: 'Unknown' } }, enum: [] } satisfies Enum;
      case 'radioButtonsEnum':
        return { value: { value: 'none', label: { en: 'Unknown' } }, enum: [] } satisfies Enum;
      case 'chart':
        throw new InvalidStateError('Tried to get chart field default value with the generic field value utility.');
      default: {
        const { type } = options satisfies never;
        console.error(`Unknown field type [${type}]!`);
        return 0;
      }
    }
  }
  return foundValue;
}

type AllowedPrimitive = 'string' | 'object' | 'number' | 'boolean';

export type ChartDataTemplate = {
  title: string;
  xRange: [number, number];
  yRange: [number, number];
  effRange?: [number, number];
  seriesLabels: string[];
  series: AlignedData;
};

function isNumberPair(value: unknown): value is [number, number] {
  return Array.isArray(value) && value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number';
}

export function isChartData(value: unknown): value is ChartDataTemplate {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<ChartDataTemplate>;

  return (
    typeof v.title === 'string' &&
    Array.isArray(v.series) &&
    Array.isArray(v.seriesLabels) &&
    isNumberPair(v.xRange) &&
    isNumberPair(v.yRange) &&
    (v.effRange === undefined || isNumberPair(v.effRange))
  );
}

export function isValidFieldTypeValue(
  expected: ValidUIField['type'],
  actual: string,
  path: string,
  actualIsArray?: boolean,
) {
  if (actual === undefined || actual === 'undefined') {
    return true;
  }

  let primitiveType: AllowedPrimitive | AllowedPrimitive[] | undefined = undefined;
  let arrayOrObject: 'both' | 'object' | 'array' = 'object';
  switch (expected) {
    case 'date':
    case 'time':
    case 'select':
    case 'text':
    case 'textarea':
    case 'radioButtons':
    case 'bookmark':
      primitiveType = 'string';
      break;
    case 'measurement':
      primitiveType = 'object';
      break;
    case 'number':
    case 'selectNumber':
      primitiveType = 'number';
      break;
    case 'toggle':
      primitiveType = 'boolean';
      break;
    case 'relationship':
      primitiveType = ['string', 'object'];
      arrayOrObject = 'array';
      break;
    case 'selectEnum':
    case 'radioButtonsEnum':
      primitiveType = ['object'];
      break;
    case 'module':
      primitiveType = 'object';
      break;
    case 'chart':
      primitiveType = 'object';
      arrayOrObject = 'both';
      break;
    default:
      throw new InvalidPathValueError('Unknown expected field type', {
        path,
        expectedType: expected satisfies never,
        receivedType: actual,
      });
  }

  if (Array.isArray(primitiveType) ? !primitiveType.includes(actual as AllowedPrimitive) : primitiveType !== actual) {
    throw new InvalidPathValueError('Field value type does not match expected value defined in ui field data.', {
      path,
      expectedType: `${expected} (${JSON.stringify(primitiveType)})`,
      receivedType: actual,
    });
  }

  const isObject =
    actual === 'object' &&
    (Array.isArray(primitiveType) ? primitiveType.includes('object') : primitiveType === 'object');

  if (isObject) {
    if (actualIsArray && arrayOrObject === 'object') {
      throw new InvalidPathValueError('Field value type does not match expected value defined in ui field data.', {
        path,
        expectedType: `${expected} (${JSON.stringify(primitiveType)}, object)`,
        receivedType: `${actual} (array)`,
      });
    }

    if (!actualIsArray && arrayOrObject === 'array') {
      throw new InvalidPathValueError('Field value type does not match expected value defined in ui field data.', {
        path,
        expectedType: `${expected} (${JSON.stringify(primitiveType)}, array)`,
        receivedType: `${actual} (object)`,
      });
    }
  }

  return true;
}

export function isValidComplexFieldValue(
  field: ValidUIField,
  value: unknown,
): value is string | boolean | number | object | undefined {
  const { type: expected, path, derived } = field;

  if (derived && (value === undefined || value === null)) {
    return true;
  }

  switch (expected) {
    case 'number':
    case 'text':
    case 'textarea':
    case 'date':
    case 'time':
    case 'toggle':
    case 'radioButtons':
    case 'select':
    case 'selectNumber':
    case 'relationship':
    case 'bookmark':
      return true;
    case 'selectEnum':
    case 'radioButtonsEnum':
      if (!isEnum(value)) {
        throw new InvalidPathValueError('Value is not a valid enum', {
          path,
          expectedType: expected,
          receivedType: typeof value,
        });
      }
      return true;
    case 'module':
      if (!isModuleOptions(value)) {
        throw new InvalidPathValueError('Value is not a valid module options object', {
          path,
          expectedType: expected,
          receivedType: typeof value,
        });
      }
      return true;
    case 'measurement':
      if (!isMeasurement(value)) {
        throw new InvalidPathValueError('Value is not a valid measurement', {
          path,
          expectedType: expected,
          receivedType: typeof value,
        });
      }
      return true;
    case 'chart':
      if (!Array.isArray(value) && !isChartData(value)) {
        throw new InvalidPathValueError('Value is not a valid chart data array', {
          path,
          expectedType: expected,
          receivedType: typeof value,
        });
      }
      return true;
    default:
      throw new InvalidStateError(`Unhandled expected UIField type ${expected satisfies never}`);
  }
}
export function getDefaultUnit(measurementType: MeasurementType): UnitOfMeasurement<typeof measurementType> {
  switch (measurementType) {
    case 'energy':
      return 'kWh';
    case 'power':
      return 'kW';
    case 'mass':
      return 'kg';
    case 'temperature':
      return 'c';
    case 'volume':
      return 'l';
    case 'area':
      return 'm2';
    case 'other':
      return '';
    case undefined:
    case 'length':
      return 'm';
    case 'volumetricFlowRate':
      return 'm^3/h';
    case 'pressure':
      return 'Pa';
  }
}

type JsonPathPartScope = {
  scope: 'child';
  operation: 'subscript' | 'member';
};

type JsonPathPart<U extends boolean = false> = {
  expression:
    | {
        type: 'root';
        value: '$';
      }
    | {
        type: 'identifier';
        value: 'string';
      }
    | {
        type: 'string_literal';
        value: 'string';
      }
    | {
        type: 'union';
        value: JsonPathPart<true>[];
      };
} & (U extends false ? JsonPathPartScope : Record<never, never>);

export function createDataPath<D extends object, T extends MeasurementType>(
  data: D,
  path: string,
  placeholder?: string | number | boolean | Measurement<T> | string[] | Enum | ModuleOptions,
): boolean {
  const parts = jp.parse(path) as JsonPathPart[];

  const hasUnion = parts.some((val) => val.expression?.type === 'union');
  if (!hasUnion) {
    return createDataPathSingle(data, path, placeholder);
  }

  const allPaths: JsonPathPart[][] = [[]];

  let currentPart = parts.shift();
  if (!currentPart) return false;
  // Handle root explictily so we don't need separate handling for emtpy array in the loop
  allPaths[0].push(currentPart);
  currentPart = parts.shift();
  while (currentPart) {
    if (currentPart.expression?.type !== 'union') {
      for (const currentPath of allPaths) {
        currentPath.push(structuredClone(currentPart));
      }
    } else if (currentPart.expression.type === 'union') {
      // Expand all path combinations for this union by cloning each existing path per union variant.
      const expandedPaths: JsonPathPart[][] = [];
      for (const currentPath of allPaths) {
        for (const variant of currentPart.expression.value) {
          const nextPath = structuredClone(currentPath);
          const variantPart = structuredClone(currentPart);
          variantPart.expression = structuredClone(variant.expression);
          expandedPaths.push([...nextPath, variantPart]);
        }
      }

      allPaths.splice(0, allPaths.length, ...expandedPaths);
    }

    currentPart = parts.shift();
  }

  return allPaths
    .map((partsPath) => jp.stringify(partsPath as unknown as Parameters<typeof jp.stringify>[0]))
    .map((singlePath) => createDataPathSingle(data, singlePath, placeholder))
    .some(Boolean);
}
/**
 * Creates the given json path in the data object if it does not exist.
 * The value will be created as an empty object
 * Updates the data in place.
 * @param data to query with the path and update
 * @param path path to create
 * @returns boolean indicating if path was needed to be created
 */
export function createDataPathSingle<D extends object, T extends MeasurementType>(
  data: D,
  path: string,
  placeholder?: string | number | boolean | Measurement<T> | string[] | Enum | ModuleOptions,
): boolean {
  const results = jp.query(data, path);
  if (results.length > 0) {
    // The path was found, no need to do anything
    return false;
  }
  const parts = jp.parse(path);
  let current = null;
  const truncParts = simpleClone(parts);
  const removedParts = [];
  // Walk the path up, removing parts of the path as long as a value is not found
  do {
    removedParts.push(truncParts.pop());
    current = jp.query(data, jp.stringify(truncParts));
  } while (!current || current.length < 1);

  // Walk the path back down and create empty object on each part
  do {
    // This holds the info on the member property we need to create
    const currentPart = removedParts.pop();

    const isLastPathPart = removedParts.length === 0;

    // Construct the json path for the parent object
    const parentPath = jp.stringify(truncParts);

    if (parentPath === '$') {
      // Handle root explicitly, can't apply directly to root as it would replace that.
      if (currentPart?.expression?.type !== 'identifier' && currentPart?.expression?.type !== 'string_literal') {
        console.warn(
          `Unknown json path expression type ${JSON.stringify(currentPart?.expression?.type)}, [${parentPath}]`,
        );
        continue;
      }
      if (currentPart?.expression?.value === undefined) {
        console.warn(`Missing value from jsonpath part [${parentPath}]`);
        continue;
      }

      // @ts-expect-error Generics are read only
      data[currentPart.expression.value] = isLastPathPart && placeholder !== undefined ? placeholder : {};
    } else if (
      typeof currentPart.expression.value === 'string' &&
      currentPart.expression.value.match(REGEX_UUID) &&
      !isLastPathPart &&
      !parentPath.endsWith('runMetadata')
    ) {
      // Do not create devices unless the path is directly pointing at it. E.g. do not allow creation of device when
      // it is missing from the data and jsonpath is pointing to some specific value inside it.
      break;
    } else {
      // Create the child under the parent
      jp.apply(data, parentPath, (parentProp) => {
        // This creates the final target value as an empty object as well.
        // Should not matter, as these in theory area always replace operations, which does not look
        // at the current value.
        if (
          currentPart?.expression?.type !== 'identifier' &&
          currentPart?.expression?.type !== 'string_literal' &&
          currentPart?.expression?.type !== 'numeric_literal'
        ) {
          console.warn(
            `Unknown json path expression type ${JSON.stringify(currentPart?.expression?.type)}, [${parentPath}]`,
          );
          return parentProp;
        }
        if (currentPart?.expression?.value === undefined) {
          console.warn(`Missing value from jsonpath part [${parentPath}]`);
          return parentProp;
        }

        if (currentPart.expression.type === 'numeric_literal') {
          // This should be an array, insert an empty object into the array until we get to the probed position
          // If the parent props is an empty object, assume it has been created previously and replace it with an array.
          if (!isArrayOrEmptyObject(parentProp)) return parentProp;
          if (typeof currentPart.expression.value !== 'number' || Number.isNaN(currentPart.expression.value))
            return parentProp;
          if (currentPart.expression.value < 0) return parentProp;
          const newArray = [];
          for (let i = 0; i <= currentPart.expression.value; i++) {
            // If this is the last path part, we can use the placeholder value, as it is intended for this level.
            // Otherwise, use an empty object, so we can go inside that to create the next deeper level.
            newArray[i] = parentProp[i] ?? (isLastPathPart ? placeholder : {}) ?? {};
          }
          return newArray;
        }

        return {
          ...parentProp,
          // Same thing here with the placeholder as above
          [currentPart.expression.value]: isLastPathPart ? (placeholder ?? {}) : {},
        };
      });
    }
    // Go one step down in the path
    truncParts.push(currentPart);
  } while (removedParts.length > 0);

  return true;
}

export function updateDataWithPath<D extends object, T extends MeasurementType>(
  data: D,
  path: string,
  value: boolean | string | number | Measurement<T> | string[] | Enum | ModuleOptions,
  onUpdate: (data: D) => void,
): void {
  const clone = simpleClone(data);
  createDataPath(clone, path, getPlaceholderValue(value));

  if (path.includes('[') || path.charAt(0) !== '$') {
    const res = jp.apply(clone, path, () => value);
    if (res.length > 0) onUpdate(clone);
  } else {
    const res = simpleJsonPathApply(clone, path, value);
    if (res) onUpdate(clone);
  }
}

// For simple jsonpaths, this feels slightly faster than usual jp.apply
function simpleJsonPathApply<T extends object>(data: T, path: string, value: any): boolean {
  const parts = path.split('.');
  let i = 1;
  let part = parts[i];
  let current = data;
  while (current && part && i <= parts.length - 2) {
    if (i <= parts.length - 3) {
      // @ts-expect-error untyped!
      current = current[part];
    }
    i++;
    part = parts[i];
  }
  // we have more than one path part left, so the full path could not be created, no updates
  if (parts[i + 1]) return false;
  // @ts-expect-error untyped!
  current[part] = value;
  return true;
}

export function updateDataWithPathOperation<D extends object>(
  data: D,
  path: string,
  operation: string,
  value: unknown,
) {
  let pathCreated = false;
  try {
    pathCreated = createDataPath(data, path);
  } catch (e) {
    console.log(`Failed to create data path for ${path}, skipping. Original error: `, e);
    return;
  }

  return jp.apply(data, path, (currentValue) => {
    const op = operation.toLowerCase();
    switch (op) {
      case 'undefined':
      case 'replace':
        return value;
      case 'append':
        return applyAppend(currentValue, value, pathCreated, path);
      case 'merge':
        return applyMerge(currentValue, value);
      case 'removekey':
        return applyRemoveKey(currentValue, value, path);
      case 'removevalue':
        return applyRemoveValue(currentValue, value);
      case 'removekeys':
        return applyRemoveKeys(currentValue, value, path);
      case 'removevalues':
        return applyRemoveValues(currentValue, value, path);
      case 'insert':
        return applyInsert(currentValue, value, pathCreated, path);
      default:
        console.warn(`Unknown update operation type [${operation}]!`);
        return currentValue;
    }
  });
}

export function splitFieldEquationOutputs(outputs: PropertiesEquationOutputOperation[]) {
  const itemOutputs: PropertiesEquationOutputOperation[] = [];
  const projectWarningOutputs: PropertiesEquationOutputOperation[] = [];

  for (const output of outputs) {
    if (output.jsonPath === '$project.warnings') {
      projectWarningOutputs.push({ ...output, jsonPath: '$.warnings' });
    } else if (output.jsonPath.startsWith('$project')) {
      throw new InvalidPathValueError('UI field equations can only update project warnings.', {
        path: output.jsonPath,
      });
    } else {
      itemOutputs.push(output);
    }
  }

  return { itemOutputs, projectWarningOutputs };
}

/**
 * Project warnings with the warning updates staged by open element drawers applied, without modifying the input.
 */
export function applyPendingWarningOperations<W extends object>(
  warnings: W | null | undefined,
  pendingOperations: Record<string, PropertiesEquationOutputOperation[]>,
): W | null | undefined {
  const operations = Object.values(pendingOperations).flat();
  if (operations.length === 0) return warnings;

  const preview = { warnings: simpleClone(warnings ?? {}) };
  for (const output of operations) {
    updateDataWithPathOperation(preview, output.jsonPath, output.operation, output.value);
  }
  return preview.warnings as W;
}

export function updateDataWithPathOperations<D extends object>(
  data: D,
  operations: PropertiesEquationOutputOperation[],
  onUpdate: (data: D) => void,
) {
  let updates = false;
  const clone = simpleClone(data);
  for (const output of operations) {
    const changed = updateDataWithPathOperation(clone, output.jsonPath, output.operation, output.value);
    if (changed && changed.length > 0) updates = true;
  }
  if (updates) {
    onUpdate(clone);
  }
}

function getPlaceholderValue<
  M extends MeasurementType,
  T extends boolean | string | number | Measurement<M> | string[] | undefined | Enum | ModuleOptions,
>(value: T): T {
  if (typeof value === 'boolean') {
    return false as T;
  }

  if (typeof value === 'number') {
    return 0 as T;
  }

  if (typeof value === 'string') {
    return '' as T;
  }

  if (Array.isArray(value)) {
    return [] as unknown as T;
  }

  if (typeof value === 'object' && isEnum(value)) {
    return {
      ...value,
      value: null,
    };
  }

  if (typeof value === 'object' && isModuleOptions(value)) {
    return {
      ...value,
      modulesSelected: [],
    } as ModuleOptions as T;
  }

  if (typeof value === 'object' && isMeasurement(value)) {
    return {
      ...value,
      value: 0,
    };
  }

  return value;
}

function isArrayOrEmptyObject(val: unknown): boolean {
  if (Array.isArray(val)) return true;
  if (val === null) return false;
  if (typeof val === 'object') {
    return Object.keys(val).length === 0;
  }

  return false;
}

const UIElementTypes = new Set(['twoColMeasurement', 'twoColHeader']);
export function isValidUIField(val: UIBasePart): val is ValidUIField {
  return !UIElementTypes.has(val.type);
}
export function isValidUIElement(val: UIBasePart): val is ValidUIElement {
  return UIElementTypes.has(val.type);
}

export type DataUpdate = <M extends MeasurementType>(
  path: string,
  value: boolean | string | number | Measurement<M> | string[] | Enum | ModuleOptions,
) => void;
